import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, ShelfGrid, orderNoShort, renderQrPngDataUrl } from '@cuito/ui';
import { catalogApi } from '../../api/catalog';
import {
  shelvesApi,
  type GridCellView,
  type GridShelfView,
  type LocationGridView,
} from '../../api/shelves';
import { readServerMessage } from '../../lib/apiError';
import {
  AMOUNT_UNAVAILABLE_TEXT,
  PRICE_UNAVAILABLE_TEXT,
  formatCents,
  formatServerYuan,
  yuanToCents,
} from '../../lib/schemeForm';
import { toUiShelf } from '../../lib/shelfForm';
import { resolveProductCode } from '../api/codes';
import { createStaffOrder, type CreatedOrder } from '../api/orders';
import { useCashierStream } from '../CashierLayout';
import { formatDataAsOf, freshnessOf, isStale, type DataFreshness } from '../data/dataAsOf';

/**
 * 手动建单（§3.3 `/cashier/new`）。
 *
 * 取数路径与单元格编辑器**完全一致**（`shelvesApi.getGrid` + `lib/shelfForm.toUiShelf`）：
 * 手动建单看到的货架必须与货架管理页是同一份服务端投影，两处各拼一次几何迟早漂移。
 *
 * **金额纪律：前端不算价。**
 *   · 提交前合计只有一种形态 —— 占位文案 `提交后由服务端计价`；
 *   · 提交后显示的是 `POST /orders/staff` 201 响应里的 `totalAmount` / `lines[].unitPrice` /
 *     `appliedPromotions`（手动单与顾客单走**同一条服务端定价路径**，前端不实现第二套算法）；
 *   · 请求体只有 `{ locationId, items: [{ productId, quantity, shelfId? }] }`（契约 §12.2）——
 *     **没有金额、没有 `schemeId`**；
 *   · 行内单价只展示服务端投影里**确实存在**的价格（网格格子的 `price`、方案定义的
 *     `items[].originalPriceCents`、商品码解析结果里的 `product.price`）。三者都没有时显示
 *     「价格以服务端为准」，**不编一个 `¥0.00`** —— 假金额比不显示更坏。
 *
 * ⚠️ 根锚点 `cashier-new-order-page` **保留**：`routes.spec.tsx` 按它逐条验证六条路由都存在。
 */
interface DraftLine {
  productId: string;
  name: string;
  shelfId: string | null;
  shelfCode: string | null;
  quantity: number;
  maxQuantity: number;
  /** 服务端投影里的单价（分）；**读不到就是 `null`** —— 前端不猜、也不补 0 */
  unitPriceCents: number | null;
  schemeTitle: string | null;
}

/** 地点还没到位时的如实提示（地点一到就收掉它，见下面的 effect） */
const NO_LOCATION_TEXT = '请先选择地点';

/** 单品格子的加购上限：摆放数量与地点可用量取小，再叠加购买上限（体验层，权威在服务端） */
function cellLimit(cell: GridCellView): number {
  const base = Math.min(cell.shelfQuantity ?? cell.availableQty, cell.availableQty);
  return cell.purchaseLimit === null ? base : Math.min(base, cell.purchaseLimit);
}

export default function CashierNewOrderPage() {
  const navigate = useNavigate();
  /**
   * 地点从**布局的流上下文**订阅，不在挂载时快照 `readCashierLocationId()`（Task 24 验收缺陷 3
   * 与 Task 18 偏离 ⑥，本任务一并收口）。
   *
   * 挂载快照有两个后果：冷启动深链（挂载时地点还是 `null`）下网格永远取不回来；
   * 顶栏**切换地点**后本页仍按旧地点取数/建单。订阅之后，布局在 `GET /locations` 回来
   * 或店员切换地点时写入上下文 ⇒ 本页自动重渲染。
   */
  const { locationId } = useCashierStream();
  const [view, setView] = useState<LocationGridView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cart, setCart] = useState<DraftLine[]>([]);
  const [selectedShelf, setSelectedShelf] = useState<GridShelfView | null>(null);
  const [productCode, setProductCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreatedOrder | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  /**
   * 货架网格数据的**接收时刻**（R-06 ④、§6.8 的 `grid-data-asof` 行）。
   *
   * 数据源是 `GET /locations/:id/grid` 的**响应到达时刻**（不是格子行的 `updatedAt`，
   * 也不是商品的任何时间字段）：`grid` 响应本身**没有**时间戳字段（契约 §18.1 末段明确不加），
   * 「这份货架有多旧」只能由本机在到手那一刻自己记。
   */
  const [gridFreshness, setGridFreshness] = useState<DataFreshness | null>(null);

  const load = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const grid = await shelvesApi.getGrid(id);
      setView(grid);
      // ★ 记录时刻在**赋值之前**：先记再渲染，面板上的时间戳与屏幕上的货架是同一份数据
      setGridFreshness(freshnessOf(new Date(), null));
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 已经取过数的地点。
   *
   * 取数只在**地点首次可用**时发生：挂载时地点就在 ⇒ 立刻取（既有行为）；冷启动深链时
   * 地点后到 ⇒ 到了再取（缺陷 3 的修法）。顶栏**切换**地点**不**重取 —— Task 18 的冻结断言
   * （`routes.spec.tsx`「模式切换不动购物车、也不发请求」）要求切换本身零请求，
   * 而原实现（挂载快照）本来也不会因为切换重取，这里只是把「首次可用」与「切换」两个时机
   * 写成显式判据，不再靠快照的副作用。
   */
  const loadedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (locationId === null) {
      setError(NO_LOCATION_TEXT);
      setLoading(false);
      return;
    }
    // 地点到位：把「请先选择地点」这条陈旧的提示收掉（地点已经在顶栏上了，那句话就是假的）
    setError((current) => (current === NO_LOCATION_TEXT ? null : current));
    if (loadedForRef.current !== null) return;
    loadedForRef.current = locationId;
    void load(locationId);
  }, [load, locationId]);

  /** 合并加购：同一个 productId 累加，上限取更严的那个（方案展开出的行按「可售份数 × 方案内件数」估） */
  const addLine = useCallback((line: Omit<DraftLine, 'quantity'>): void => {
    setCart((current) => {
      const existing = current.find((entry) => entry.productId === line.productId);
      if (existing === undefined) return [...current, { ...line, quantity: 1 }];
      if (existing.quantity + 1 > existing.maxQuantity) return current;
      return current.map((entry) =>
        entry.productId === line.productId ? { ...entry, quantity: entry.quantity + 1 } : entry,
      );
    });
  }, []);

  /**
   * 方案格子 → 单品行。
   *
   * 方案不持有库存、也不是商品，而 `POST /orders/staff` 的 `items[].productId` **只收单品**
   * （§12.1 的同款约束）—— 网格投影里的方案格子只有 `{ id, title, priceCents, … }`、**没有组成**，
   * 所以组成只能从 `GET /promotion-schemes` 的 `items[]` 取（`productId` / `quantity`）。
   * 不展开的话「点方案格子加购」只能加出一个不存在的 productId（必然 404/400）。
   *
   * 展开出的行保留方案名**只作展示**：优惠由服务端在下单时计算并回填
   * （`appliedPromotions` + 逐行 `unitPrice`），前端不预先算「组合价」。
   */
  const addScheme = useCallback(
    async (cell: GridCellView): Promise<void> => {
      if (cell.scheme === null) return;
      try {
        const schemes = await catalogApi.listSchemes({ active: true, pageSize: 100 });
        const scheme = schemes.items.find((entry) => entry.id === cell.scheme?.id);
        if (scheme === undefined) {
          setError('该促销方案已失效，请刷新货架');
          return;
        }
        for (const item of scheme.items) {
          for (let count = 0; count < item.quantity; count += 1) {
            addLine({
              productId: item.productId,
              name: item.productName,
              shelfId: selectedShelf?.shelfId ?? null,
              shelfCode: selectedShelf?.code ?? null,
              // 上限按「可售份数 × 方案内件数」估：这是体验层，服务端的定价与库存校验才是权威
              maxQuantity: Math.max(1, cell.availableQty * item.quantity),
              unitPriceCents: item.originalPriceCents,
              schemeTitle: cell.scheme.title,
            });
          }
        }
        setNotice(`已加入方案内 ${scheme.items.length} 个商品，优惠由服务端计算`);
      } catch (e) {
        setError(readServerMessage(e) ?? '方案加载失败，请重试');
      }
    },
    [addLine, selectedShelf],
  );

  const scan = useCallback(async (): Promise<void> => {
    if (locationId === null) {
      setError(NO_LOCATION_TEXT);
      return;
    }
    const code = productCode.trim();
    if (code === '') {
      setError('无法识别的商品码');
      return;
    }
    setError(null);
    setNotice(null);
    try {
      const result = await resolveProductCode(code, { locationId });
      if (!result.found || result.atLocation === null) {
        setError('该商品不在本地点销售');
        return;
      }
      addLine({
        productId: result.product.id,
        name: result.product.name,
        shelfId: result.atLocation.shelf.shelfId,
        shelfCode: result.atLocation.shelf.code,
        maxQuantity: Math.max(
          1,
          result.atLocation.purchaseLimit === null
            ? Math.min(result.atLocation.shelfQuantity, result.atLocation.availableQty)
            : Math.min(result.atLocation.shelfQuantity, result.atLocation.availableQty, result.atLocation.purchaseLimit),
        ),
        unitPriceCents: result.product.price === undefined ? null : yuanToCents(result.product.price),
        schemeTitle: null,
      });
      setProductCode('');
      setNotice('已加入购物车');
    } catch (e) {
      setError(readServerMessage(e) ?? '无法识别的商品码');
    }
  }, [addLine, locationId, productCode]);

  const submit = useCallback(async (): Promise<void> => {
    if (locationId === null || cart.length === 0) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const order = await createStaffOrder({
        locationId,
        items: cart.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
          ...(line.shelfId === null ? {} : { shelfId: line.shelfId }),
        })),
      });
      setCreated(order);
      setCart([]);
      setSelectedShelf(null);
    } catch (e) {
      // 失败**保留购物车**：库存不足/超限购的正确出路是改数量重试，清空等于让店员重挑一遍
      setError(readServerMessage(e) ?? '商品库存不足，无法建单');
    } finally {
      setSubmitting(false);
    }
  }, [cart, locationId]);

  // 顾客订单码：用第一页载荷现场渲染（`renderQrPngDataUrl` 是 `packages/ui` 的既有出口）
  useEffect(() => {
    const first = created?.orderCode.pages[0]?.payload;
    if (first === undefined) {
      setQr(null);
      return;
    }
    let cancelled = false;
    void renderQrPngDataUrl(first)
      .then((url) => {
        if (!cancelled) setQr(url);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [created]);

  const uiShelves = useMemo(() => (view?.shelves ?? []).map(toUiShelf), [view]);
  const itemCount = cart.reduce((sum, line) => sum + line.quantity, 0);

  if (loading) {
    return <p className="py-8 text-center text-gray-500">加载中…</p>;
  }

  return (
    <div data-testid="cashier-new-order-page" className="flex flex-col gap-3">
      <h1 className="text-2xl font-bold text-gray-900">手动建单</h1>

      {error !== null && (
        <p data-testid="new-order-error" role="alert" className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </p>
      )}
      {notice !== null && (
        <p data-testid="new-order-notice" className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">
          {notice}
        </p>
      )}

      {created === null ? (
        <>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              label="商品码"
              className="w-56"
              data-testid="new-order-product-code"
              value={productCode}
              onChange={(event) => setProductCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void scan();
              }}
            />
            <Button data-testid="new-order-scan-confirm" variant="secondary" onClick={() => void scan()}>
              扫码加购
            </Button>
          </div>

          {view !== null && view.shelves.length === 0 && (
            <p className="text-gray-500">该地点还没有上架商品，无法手动建单</p>
          )}

          {view !== null && view.shelves.length > 0 && (
            <>
              {/*
               * 货架网格的标题行 + **数据时间戳**（§6.8 的 `grid-data-asof`：
               * 「手动建单页的货架网格标题行」，文案 `数据时间 HH:mm:ss`，超 5 分钟转 `text-amber-600`）。
               */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold">货架</span>
                <span
                  data-testid="grid-data-asof"
                  className={
                    isStale(gridFreshness, new Date())
                      ? 'text-xs text-amber-600'
                      : 'text-xs text-gray-500'
                  }
                >
                  {formatDataAsOf(gridFreshness)}
                </span>
              </div>
              <div className="h-[50vh] w-full overflow-hidden rounded-lg border-2 border-black p-4">
                <ShelfGrid
                  rows={view.grid.rows}
                  cols={view.grid.cols}
                  shelves={uiShelves}
                  mode="pick"
                  onSelectShelf={(shelfId) =>
                    setSelectedShelf(view.shelves.find((shelf) => shelf.shelfId === shelfId) ?? null)
                  }
                />
              </div>
            </>
          )}

          <section data-testid="new-order-cart" className="flex flex-col gap-2 rounded-lg border-2 border-black p-3">
            {/*
             * 件数 ≠ 行数：同一商品连点两次是**一行两件**（件数按各行 quantity 求和，
             * 行数只有下面的列表才知道）—— §12.2 的 `itemCount` 与 `lineCount` 也是这个区别。
             */}
            <h2 data-testid="new-order-cart-count" className="text-lg font-bold">
              购物车（{itemCount} 件）
            </h2>
            {cart.length === 0 ? (
              <p className="py-4 text-center text-gray-500">还没有选择商品</p>
            ) : (
              cart.map((line) => (
                <div
                  key={line.productId}
                  data-testid="new-order-cart-line"
                  className="flex items-center gap-3 rounded-lg border-2 border-black p-2"
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-base font-bold">{line.name}</span>
                    {/* §6.9 ②：命中方案的行在商品名下追加一行方案名（`text-xs text-blue-700`） */}
                    {line.schemeTitle !== null && (
                      <span data-testid="new-order-line-scheme" className="text-xs text-blue-700">
                        {line.schemeTitle}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      货架 {line.shelfCode ?? '-'} ·{' '}
                      {line.unitPriceCents === null ? PRICE_UNAVAILABLE_TEXT : formatCents(line.unitPriceCents)}
                    </span>
                  </div>
                  {/* §6.7 的三键，class 逐字：`-` / 数量 / `+`（到上限 disabled）/ `✕` */}
                  <button
                    type="button"
                    aria-label="减少数量"
                    onClick={() =>
                      setCart((current) =>
                        line.quantity <= 1
                          ? current.filter((entry) => entry.productId !== line.productId)
                          : current.map((entry) =>
                              entry.productId === line.productId ? { ...entry, quantity: entry.quantity - 1 } : entry,
                            ),
                      )
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black text-xl font-bold active:bg-gray-100"
                  >
                    −
                  </button>
                  <span className="min-w-[24px] text-center text-lg font-bold">{line.quantity}</span>
                  <button
                    type="button"
                    aria-label="增加数量"
                    title={line.quantity >= line.maxQuantity ? '已达上限' : undefined}
                    disabled={line.quantity >= line.maxQuantity}
                    onClick={() =>
                      setCart((current) =>
                        current.map((entry) =>
                          entry.productId === line.productId && entry.quantity < entry.maxQuantity
                            ? { ...entry, quantity: entry.quantity + 1 }
                            : entry,
                        ),
                      )
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black text-xl font-bold active:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-400"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    aria-label="移除"
                    onClick={() => setCart((current) => current.filter((entry) => entry.productId !== line.productId))}
                    className="ml-1 flex h-8 w-8 items-center justify-center rounded-lg border-2 border-red-500 text-xs font-bold text-red-500 active:bg-red-50"
                  >
                    ✕
                  </button>
                </div>
              ))
            )}

            <div className="flex items-center justify-between border-t-2 border-black pt-3">
              {/* 提交前**不算价**：合计只有服务端说了算 */}
              <span data-testid="new-order-total" className="text-xl font-bold">
                提交后由服务端计价
              </span>
              <Button
                data-testid="new-order-submit"
                size="lg"
                disabled={cart.length === 0 || submitting}
                onClick={() => void submit()}
              >
                {submitting ? '建单中…' : '提交建单'}
              </Button>
            </div>
          </section>
        </>
      ) : (
        <section data-testid="new-order-created" className="flex flex-col items-center gap-3 rounded-lg border-2 border-black p-4">
          <p className="text-lg font-bold">请让顾客扫描订单码</p>
          <p className="text-base font-bold">订单 {orderNoShort(created.orderNo)}</p>
          <p className="text-2xl font-bold">短码 {created.shortCode}</p>
          {qr === null ? (
            <div className="aspect-square w-64 animate-pulse rounded-lg bg-gray-100" />
          ) : (
            <img data-testid="new-order-qr" src={qr} alt="订单码" className="w-64 object-contain" />
          )}
          {created.orderCode.totalPages > 1 && (
            <p className="text-sm text-gray-500">共 {created.orderCode.totalPages} 个码，请让顾客依次扫描</p>
          )}
          {/*
           * 逐行展示的是**服务端结算后的** `lines[].unitPrice`（手动单与顾客单同一条定价路径），
           * 不是购物车里的原价；划价对照取响应的 `originalPrice`（§6.9 ④：实付价 + 划价）。
           */}
          <ul className="w-full">
            {created.lines.map((line) => (
              <li
                key={line.id}
                data-testid="new-order-created-line"
                className="flex items-center justify-between border-t border-gray-200 py-1 text-sm"
              >
                <span className="font-bold">
                  {line.productName} ×{line.quantity}
                </span>
                <span className="flex items-center gap-2">
                  {line.originalPrice !== line.unitPrice && (
                    <s className="text-gray-500">
                      {formatServerYuan(line.originalPrice) ?? PRICE_UNAVAILABLE_TEXT}
                    </s>
                  )}
                  <span className="font-bold">
                    {formatServerYuan(line.unitPrice) ?? PRICE_UNAVAILABLE_TEXT}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p data-testid="new-order-total" className="text-xl font-bold">
            {formatServerYuan(created.totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT}
          </p>
          {created.appliedPromotions.length > 0 && (
            <p data-testid="new-order-promotions" className="text-sm font-bold">
              {created.appliedPromotions
                .map(
                  (promotion) =>
                    `已自动应用：${promotion.schemeTitle}，省 ${formatCents(promotion.discountCents)}`,
                )
                .join('；')}
            </p>
          )}
          <Button onClick={() => navigate('/cashier')}>回到接单面板</Button>
        </section>
      )}

      {/* 格子列表：复用货架管理页的取数路径（同一个网格响应），但只读 + 加购 */}
      <Modal
        open={selectedShelf !== null}
        onClose={() => setSelectedShelf(null)}
        title={selectedShelf === null ? '' : `${selectedShelf.label ?? selectedShelf.code} · ${selectedShelf.code}`}
        maxWidth="max-w-2xl"
      >
        {selectedShelf !== null && (
          <div data-testid="cell-picker" className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
            {selectedShelf.items.length === 0 ? (
              <p className="py-8 text-center text-lg text-gray-500">该位置暂无商品</p>
            ) : (
              selectedShelf.items.map((cell) => (
                <div key={cell.shelfItemId} className="flex items-center gap-3 rounded-lg border-2 border-black p-2">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-base font-bold">
                      {cell.kind === 'PROMOTION' && (
                        <span className="mr-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">组合促销</span>
                      )}
                      {cell.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      货架 {selectedShelf.code} ·{' '}
                      {formatServerYuan(cell.price) ?? PRICE_UNAVAILABLE_TEXT} · 可用 {cell.availableQty} 件
                      {cell.scheme === null ? '' : ` · 方案价 ${formatCents(cell.scheme.priceCents)}`}
                    </span>
                  </div>
                  <Button
                    onClick={() => {
                      if (cell.kind === 'PROMOTION') {
                        void addScheme(cell);
                        setSelectedShelf(null);
                        return;
                      }
                      if (cell.productId === null) return;
                      addLine({
                        productId: cell.productId,
                        name: cell.name,
                        shelfId: selectedShelf.shelfId,
                        shelfCode: selectedShelf.code,
                        maxQuantity: Math.max(1, cellLimit(cell)),
                        unitPriceCents: yuanToCents(cell.price),
                        schemeTitle: null,
                      });
                    }}
                  >
                    加购 {cell.name}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
